import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { tokens } from '../../global/theme/tokens';

// above this many options a list of radios / checkboxes becomes a dropdown
const DROPDOWN_FROM = 6;

const emptyAnswer = { value: null, text: '', other: false };

/**
 * Whether an answer says anything: a picked option, yes/no or free text.
 */
export const isAnswered = (answer) =>
  !!answer &&
  ((Array.isArray(answer.value) ? answer.value.length > 0 : answer.value !== null) ||
    answer.text.trim() !== '');

/**
 * A paused turn: the AI asked questions (ask_user) and waits for the
 * answers. Every question can be answered in free text as well, whatever
 * its type, and may be left unanswered. The answers of all requests are
 * sent together; "Skip" resumes the turn without answers.
 */
export const AiChatQuestionCard = ({ requests, canDecide, onDecide }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  // answers[request id][question id] = { value, text, other }
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // see AiChatApprovalCard: onDecide may re-render the panel synchronously
  const sentRef = useRef(null);

  const ids = requests.map((r) => r.id).join(',');
  useEffect(() => {
    setAnswers({});
    setSubmitting(false);
    sentRef.current = null;
  }, [ids]);

  const answerOf = (requestId, questionId) =>
    answers[requestId]?.[questionId] || emptyAnswer;

  const setAnswer = (requestId, questionId, patch) =>
    setAnswers((prev) => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        [questionId]: { ...answerOf(requestId, questionId), ...patch },
      },
    }));

  const anyAnswered = requests.some((r) =>
    (r.questions || []).some((q) => isAnswered(answerOf(r.id, q.id))),
  );

  const send = (decisions) => {
    if (sentRef.current === ids) return;
    sentRef.current = ids;
    setSubmitting(true);
    onDecide(decisions).catch(() => {
      sentRef.current = null;
      setSubmitting(false);
    });
  };

  const submit = () =>
    send(
      requests.map((r) => ({
        id: r.id,
        decision: 'answer',
        answers: (r.questions || []).map((q) => {
          const answer = answerOf(r.id, q.id);
          return {
            id: q.id,
            value: answer.value,
            text: answer.text.trim() || null,
          };
        }),
      })),
    );

  const skip = () =>
    send(requests.map((r) => ({ id: r.id, decision: 'reject' })));

  const disabled = !canDecide || submitting;

  return (
    <Box
      className="flex flex-col gap-2 px-3 py-2.5 rounded-xl"
      sx={{
        backgroundColor: `${colors.info}12`,
        border: `1px solid ${colors.info}50`,
      }}
    >
      <Box className="flex items-center gap-1.5">
        <HelpOutlineIcon sx={{ fontSize: 18, color: colors.info }} />
        <Typography variant="subtitle2" fontWeight={600} className="flex-1">
          {t('The AI has questions')}
        </Typography>
        {submitting && <CircularProgress size={14} />}
      </Box>

      {requests.map((request) =>
        (request.questions || []).map((question, index) => (
          <QuestionField
            key={`${request.id}-${question.id || index}`}
            question={question}
            answer={answerOf(request.id, question.id)}
            disabled={disabled}
            onChange={(patch) => setAnswer(request.id, question.id, patch)}
          />
        )),
      )}

      {canDecide ? (
        <Box className="flex items-center gap-1 pt-0.5">
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={disabled || !anyAnswered}
            onClick={submit}
            sx={{ textTransform: 'none', fontSize: '0.72rem', borderRadius: '8px' }}
          >
            {t('Send answers')}
          </Button>
          <Button
            size="small"
            variant="text"
            color="inherit"
            disabled={disabled}
            onClick={skip}
            sx={{
              textTransform: 'none',
              fontSize: '0.72rem',
              ml: 'auto',
              opacity: 0.7,
            }}
          >
            {t('Skip')}
          </Button>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          {t('Waiting for a participant with write access to answer.')}
        </Typography>
      )}
    </Box>
  );
};

const QuestionField = ({ question, answer, disabled, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const type = question.type || 'text';
  const options = question.options || [];

  return (
    <Box
      className="flex flex-col gap-1 px-2.5 py-2 rounded-lg"
      sx={{ backgroundColor: colors.card, border: `1px solid ${colors.bgInput}` }}
    >
      <Typography variant="body2" fontWeight={500}>
        {question.question}
      </Typography>
      {question.description && (
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {question.description}
        </Typography>
      )}

      {type === 'text' && (
        <AnswerText
          value={answer.text}
          disabled={disabled}
          onChange={(text) => onChange({ text })}
          autoFocus={false}
        />
      )}
      {type === 'boolean' && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={answer.value === null ? null : answer.value ? 'yes' : 'no'}
          disabled={disabled}
          onChange={(_, next) =>
            onChange({ value: next === null ? null : next === 'yes' })
          }
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, py: 0.25 } }}
        >
          <ToggleButton value="yes">{t('Yes')}</ToggleButton>
          <ToggleButton value="no">{t('No')}</ToggleButton>
        </ToggleButtonGroup>
      )}
      {type === 'select' && (
        <OptionPicker
          options={options}
          selected={answer.value === null ? [] : [answer.value]}
          multiple={false}
          disabled={disabled}
          onChange={(selected) => onChange({ value: selected[0] ?? null })}
        />
      )}
      {type === 'multiselect' && (
        <OptionPicker
          options={options}
          selected={Array.isArray(answer.value) ? answer.value : []}
          multiple
          disabled={disabled}
          onChange={(selected) => onChange({ value: selected })}
        />
      )}

      {type !== 'text' && (
        <>
          {!answer.other && (
            <Typography
              variant="caption"
              className="cursor-pointer select-none self-start"
              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
              onClick={() => !disabled && onChange({ other: true })}
            >
              {t('Other answer')}…
            </Typography>
          )}
          <Collapse in={answer.other} unmountOnExit>
            <AnswerText
              value={answer.text}
              disabled={disabled}
              onChange={(text) => onChange({ text })}
              autoFocus
            />
          </Collapse>
        </>
      )}
    </Box>
  );
};

const AnswerText = ({ value, disabled, onChange, autoFocus }) => {
  const { t } = useTranslation();

  return (
    <TextField
      size="small"
      multiline
      minRows={1}
      maxRows={5}
      fullWidth
      autoFocus={autoFocus}
      placeholder={t('Your answer')}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ 'aria-label': t('Your answer') }}
      sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
    />
  );
};

/**
 * One option (radios) or several (checkboxes); a dropdown when the list
 * is long.
 */
const OptionPicker = ({ options, selected, multiple, disabled, onChange }) => {
  const { t } = useTranslation();

  if (options.length >= DROPDOWN_FROM) {
    return (
      <Select
        size="small"
        multiple={multiple}
        displayEmpty
        value={multiple ? selected : (selected[0] ?? '')}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value;
          onChange(multiple ? next : next === '' ? [] : [next]);
        }}
        renderValue={(value) => {
          const picked = multiple ? value : value ? [value] : [];
          return picked.length > 0 ? (
            picked.join(', ')
          ) : (
            <span style={{ opacity: 0.6 }}>{t('Please select')}</span>
          );
        }}
        sx={{ fontSize: '0.85rem' }}
      >
        {!multiple && (
          <MenuItem value="">
            <em>{t('No selection')}</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option} value={option} dense>
            {multiple && (
              <Checkbox size="small" checked={selected.includes(option)} sx={{ p: 0, mr: 1 }} />
            )}
            {option}
          </MenuItem>
        ))}
      </Select>
    );
  }

  const toggle = (option) => {
    if (multiple) {
      onChange(
        selected.includes(option)
          ? selected.filter((o) => o !== option)
          : [...selected, option],
      );
    } else {
      onChange(selected.includes(option) ? [] : [option]);
    }
  };

  return (
    <Box className="flex flex-col">
      {options.map((option) => (
        <FormControlLabel
          key={option}
          label={option}
          disabled={disabled}
          control={
            multiple ? (
              <Checkbox
                size="small"
                checked={selected.includes(option)}
                onChange={() => toggle(option)}
              />
            ) : (
              <Radio
                size="small"
                checked={selected.includes(option)}
                onClick={() => toggle(option)}
              />
            )
          }
          sx={{
            m: 0,
            '& .MuiFormControlLabel-label': { fontSize: '0.85rem' },
            '& .MuiButtonBase-root': { py: 0.25 },
          }}
        />
      ))}
    </Box>
  );
};
